// src/utils/teamDefaults.js
// Read/write for the single team-wide defaults doc used by the
// post-acceptance email composer (our title closer + broker admin fee) and the
// checklist's Backup Client Care Person.

import { doc, getDoc, serverTimestamp } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { firestored, storage } from "../firebase/firebaseConfig";
import { setDoc, uploadBytes } from "./lookOnlyFirestore";
import { assertCanWrite } from "./lookOnly";

const DOC_PATH = ["settings", "teamDefaults"];

export const TEAM_DEFAULTS_FALLBACK = {
  titleCompany: "Watermark Title",
  titleCloserName: "Julie Davidson",
  titleCloserEmail: "",
  titleCloserPhone: "",
  brokerAdminFee: 599,
  // My Checklist (09/27/2026): who owns buyer and contract-stage items for an agent with no
  // Transaction person in Settings > Coordinators. "" = nobody (they land in Items With No Owner).
  clientCareOwnerId: "",
  // Shutterfly Book alerts (Henry 10/06/2026): the staff person told when a turn-in or listing request
  // says Yes to a Shutterfly Book. Angela Lopez always hears about it too (Henry 10/08/2026), so "" =
  // Angela Lopez and the agent's Client Care person (utils/shutterflyBook.js).
  shutterflyAlertUserId: "",
  // Closing gifts (Henry 10/06/2026, from his Closing Gifts spreadsheet): what each side normally gets,
  // plus the extras that can be added on a deal. Read through utils/closingGifts.js giftSettingsOf.
  closingGiftsSeller: ["Shutterfly Book"],
  closingGiftsBuyer: ["Doormat", "Binder"],
  closingGiftsExtras: ["Charcuterie Board", "Watercolor", "Cutting Board"],
};

export async function loadTeamDefaults() {
  try {
    const ref = doc(firestored, ...DOC_PATH);
    const snap = await getDoc(ref);
    if (!snap.exists()) return { ...TEAM_DEFAULTS_FALLBACK };
    return { ...TEAM_DEFAULTS_FALLBACK, ...snap.data() };
  } catch (err) {
    console.error("loadTeamDefaults failed:", err);
    return { ...TEAM_DEFAULTS_FALLBACK };
  }
}

export async function saveTeamDefaults(values, userId) {
  assertCanWrite();
  const ref = doc(firestored, ...DOC_PATH);
  await setDoc(ref, {
    ...values,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  }, { merge: true });
}

// Henry 10/14/2026: the closer-cropped photo the client portal shows beside a person (Settings > Team
// Defaults, Portal Photos and the vendor cards). Saved under profilePictures/portal/, a path storage.rules
// already lets any active team member write. Returns the https download address to keep in Team Defaults.
export async function uploadPortalPhoto(blob, key) {
  assertCanWrite();
  const safeKey = String(key || "photo").replace(/[^A-Za-z0-9_-]/g, "_").slice(0, 60) || "photo";
  const photoRef = ref(storage, `profilePictures/portal/${safeKey}_${Date.now()}.jpg`);
  await uploadBytes(photoRef, blob, { contentType: "image/jpeg" });
  return getDownloadURL(photoRef);
}
